/**
 * ProbabilityCalculator - Hypergeometric probability tool
 *
 * Calculate the probability of drawing specific cards in opening hands.
 * Uses hypergeometric distribution: P(X=k) = C(K,k) * C(N-K,n-k) / C(N,n)
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { IoCalculator } from 'react-icons/io5';

import InfoButton, { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

import type { EnrichedDeckCard } from '../../types';

// ============================================================================
// Types
// ============================================================================

export interface ProbabilityCalculatorProps {
  cards: EnrichedDeckCard[];
  className?: string;
}

interface ProbabilityResult {
  exactly: number[];
  atLeast: number[];
  atMost: number[];
}

// ============================================================================
// Math Helpers
// ============================================================================

/**
 * Binomial coefficient: C(n, k) = n! / (k! * (n-k)!)
 * Uses multiplicative formula to avoid overflow
 */
function binomial(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;

  // Use symmetry for efficiency
  if (k > n - k) k = n - k;

  let result = 1;
  for (let i = 0; i < k; i++) {
    result = (result * (n - i)) / (i + 1);
  }
  return Math.round(result);
}

/**
 * Hypergeometric probability: P(X = k)
 * N = population size (deck size)
 * K = success states in population (copies in deck)
 * n = number of draws (cards drawn)
 * k = observed successes (copies drawn)
 */
function hypergeometric(N: number, K: number, n: number, k: number): number {
  if (k < 0 || k > Math.min(K, n) || k < Math.max(0, n - (N - K))) return 0;

  const numerator = binomial(K, k) * binomial(N - K, n - k);
  const denominator = binomial(N, n);

  return denominator === 0 ? 0 : numerator / denominator;
}

/**
 * Calculate full probability distribution
 */
function calculateProbabilities(deckSize: number, copies: number, cardsDrawn: number): ProbabilityResult {
  const maxDrawable = Math.min(copies, cardsDrawn);
  const exactly: number[] = [];
  const atLeast: number[] = [];
  const atMost: number[] = [];

  // Calculate exact probabilities
  for (let k = 0; k <= maxDrawable; k++) {
    exactly.push(hypergeometric(deckSize, copies, cardsDrawn, k));
  }

  // Calculate cumulative probabilities
  let cumSum = 0;
  for (let k = 0; k <= maxDrawable; k++) {
    cumSum += exactly[k];
    atMost.push(cumSum);
  }

  // Calculate at least (1 - P(X < k) = 1 - P(X <= k-1))
  for (let k = 0; k <= maxDrawable; k++) {
    atLeast.push(1 - (k > 0 ? atMost[k - 1] : 0));
  }

  return { exactly, atLeast, atMost };
}

/**
 * Info content for probability calculator
 */
function ProbabilityCalculatorInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          Calculate the exact probability of drawing specific cards using <strong>hypergeometric distribution</strong> — the math behind
          card game probabilities.
        </p>
      </InfoSection>

      <InfoSection title="How to use">
        <InfoList
          items={[
            'Copies = how many of a specific card in your deck',
            'Cards Drawn = opening hand size (usually 7)',
            'Results show probability of drawing 0, 1, 2+ copies',
          ]}
        />
      </InfoSection>

      <InfoSection title="Common questions answered">
        <InfoList
          items={[
            '"How often will I see my 4-of in my opening hand?" → ~40%',
            '"What are the odds of drawing both combo pieces?" → Use at least 1 for each',
            '"Should I run 3 or 4 copies?" → Compare the at least 1 probabilities',
          ]}
        />
      </InfoSection>

      <InfoSection title="Rule of thumb">
        <InfoList
          items={[
            '4 copies → ~40% to see at least 1 in 7 cards',
            '3 copies → ~33% to see at least 1',
            '2 copies → ~23% to see at least 1',
            '1 copy → ~12% to see it',
          ]}
        />
      </InfoSection>

      <InfoTip>
        For key cards you want every game, run 4 copies. For situational cards or legendaries, 2-3 copies often suffice. The numbers
        don&apos;t lie!
      </InfoTip>
    </>
  );
}

// ============================================================================
// Component
// ============================================================================

export default function ProbabilityCalculator({ cards, className }: ProbabilityCalculatorProps): ReactElement {
  // Calculate deck size from cards
  const deckSize = useMemo(() => {
    return cards.reduce((sum, c) => {
      if (c.zone !== 'main') return sum;
      return sum + c.quantity;
    }, 0);
  }, [cards]);

  const [copies, setCopies] = useState(4);
  const [cardsDrawn, setCardsDrawn] = useState(7);

  // Clamp values
  const clampedCopies = Math.max(1, Math.min(copies, deckSize));
  const clampedDrawn = Math.max(1, Math.min(cardsDrawn, deckSize));

  // Calculate probabilities
  const results = useMemo(() => calculateProbabilities(deckSize, clampedCopies, clampedDrawn), [deckSize, clampedCopies, clampedDrawn]);

  const handleCopiesChange = useCallback((value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num)) setCopies(num);
  }, []);

  const handleDrawnChange = useCallback((value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num)) setCardsDrawn(num);
  }, []);

  // Not enough cards
  if (deckSize < 1) {
    return (
      <div className={clsx('space-y-3', className)}>
        <div className="flex items-center gap-1">
          <span className="text-ash text-xs font-medium">Probability Calculator</span>
          <InfoButton title="Hypergeometric Calculator">
            <ProbabilityCalculatorInfo />
          </InfoButton>
        </div>
        <div className="rounded-lg bg-zinc-800/30 p-4 text-center">
          <p className="text-ash text-sm">Add cards to use calculator</p>
        </div>
      </div>
    );
  }

  // Format percentage
  const formatPct = (p: number) => `${(p * 100).toFixed(1)}%`;

  return (
    <div className={clsx('space-y-3', className)}>
      {/* Header */}
      <div className="flex items-center gap-1">
        <IoCalculator className="text-aether h-4 w-4" />
        <span className="text-ash text-xs font-medium">Probability Calculator</span>
        <InfoButton title="Hypergeometric Calculator">
          <ProbabilityCalculatorInfo />
        </InfoButton>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-ash mb-1 block text-[10px] tracking-wide uppercase">Copies in Deck</label>
          <input
            type="number"
            min={1}
            max={deckSize}
            value={copies}
            onChange={(e) => handleCopiesChange(e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-ash mb-1 block text-[10px] tracking-wide uppercase">Cards Drawn</label>
          <input
            type="number"
            min={1}
            max={deckSize}
            value={cardsDrawn}
            onChange={(e) => handleDrawnChange(e.target.value)}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Deck info */}
      <div className="text-ash text-center text-xs">Deck: {deckSize} cards</div>

      {/* Results table */}
      <div className="overflow-hidden rounded-lg border border-zinc-700/50">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-700/50 bg-zinc-800/50">
              <th className="text-ash px-3 py-2 text-left font-medium">Draw</th>
              <th className="text-ash px-3 py-2 text-right font-medium">Exactly</th>
              <th className="text-ash px-3 py-2 text-right font-medium">At Least</th>
            </tr>
          </thead>
          <tbody>
            {results.exactly.map((exactProb, k) => (
              <tr
                key={k}
                className={clsx('border-b border-zinc-800/50 last:border-0', k >= 1 && results.atLeast[k] >= 0.3 && 'bg-emerald-500/5')}
              >
                <td className="text-silver px-3 py-1.5">
                  {k} {k === 1 ? 'copy' : 'copies'}
                </td>
                <td className="text-silver px-3 py-1.5 text-right font-mono">{formatPct(exactProb)}</td>
                <td
                  className={clsx(
                    'px-3 py-1.5 text-right font-mono',
                    results.atLeast[k] >= 0.5 ? 'text-emerald-400' : results.atLeast[k] >= 0.3 ? 'text-amber-300' : 'text-silver',
                  )}
                >
                  {formatPct(results.atLeast[k])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-2 text-center">
        <div className="rounded-lg bg-zinc-800/40 px-3 py-2">
          <div className={clsx('text-lg font-bold', results.atLeast[1] >= 0.4 ? 'text-emerald-400' : 'text-amber-300')}>
            {formatPct(results.atLeast[1] ?? 0)}
          </div>
          <div className="text-ash text-[10px]">See at least 1</div>
        </div>
        <div className="rounded-lg bg-zinc-800/40 px-3 py-2">
          <div className="text-pearl text-lg font-bold">{formatPct(results.exactly[0] ?? 0)}</div>
          <div className="text-ash text-[10px]">Miss completely</div>
        </div>
      </div>
    </div>
  );
}
